import {useCallback, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {differenceInMinutes, differenceInSeconds} from 'date-fns';

import {usePageVisibility} from '@shared/core';
import useInterval from '@shared/core/hooks/use-interval.hook';

import {getMeetingRoomStatusMsg} from '../../utilities/get-meeting-room-status.utility';
import {FloorContext} from '../../components/floor-plan/floor-plan.component';

const ONE_SEC = 1;
const MS_IN_SEC = 1000;
const SEC_IN_MIN = 60;
const SEC_IN_HOUR = 3600;
const MS_IN_MIN = SEC_IN_MIN * MS_IN_SEC;
const MS_IN_HOUR = SEC_IN_HOUR * MS_IN_SEC;

const calculateDelay = (diffInSecond: number): number => {
  if (diffInSecond <= 0) return 0;
  if (diffInSecond < SEC_IN_MIN) return (diffInSecond + ONE_SEC) * MS_IN_SEC;
  if (diffInSecond < SEC_IN_HOUR) return MS_IN_MIN;
  return MS_IN_HOUR;
};

export const useMeetingRoomTimer = (meetingRoomId: string, date: string) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [delay, setDelay] = useState(0);

  const {t} = useTranslation();
  const {floorsEventEmitter} = useContext(FloorContext);
  const isPageVisible = usePageVisibility();

  const updateTimer = useCallback(() => {
    if (!meetingRoomId || !date) return;

    const now = new Date();
    const end = new Date(date);
    const diffInSeconds = differenceInSeconds(end, now);
    const diffMins = differenceInMinutes(end, now);

    setTimeRemaining(getMeetingRoomStatusMsg(diffMins, t));

    if (diffInSeconds <= 0) {
      floorsEventEmitter.emit('meetingroom_status', {meetingRoomId});
      setDelay(0);
    } else {
      setDelay(calculateDelay(diffInSeconds));
    }
  }, [date, floorsEventEmitter, meetingRoomId, t]);

  useEffect(() => {
    if (meetingRoomId && !date) {
      floorsEventEmitter.emit('meetingroom_status', {meetingRoomId});
    }
  }, [meetingRoomId, date, floorsEventEmitter]);

  useEffect(() => {
    if (!meetingRoomId || !date || !isPageVisible) return;
    updateTimer();
  }, [meetingRoomId, date, updateTimer, isPageVisible]);

  useInterval(updateTimer, delay);

  return {timeRemaining};
};
